import { Injectable, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import {
  ActivitiesOptions,
  PartialActivityWhereInput,
} from '@wilson/api/gateway';
import { AuthState } from '@wilson/auth/core';
import {
  InvoiceActivityStatus,
  NO_AGREEMENT,
  OperationStatus,
  ShiftState,
} from '@wilson/interfaces';
import {
  InvoiceDateTypeFilter,
  InvoiceFilters,
} from '../interfaces/invoicing-custom-types';

export interface ShiftUserLabels {
  shiftUserLabelIds?: string[];
}
export interface ShiftLabels {
  shiftLabelIds?: string[];
}

export interface ShiftProjects {
  shiftProjectIds?: string[];
}

export interface ShiftUserProfessions {
  shiftUserProfessionIds?: string[];
}

export interface Invoiced {
  invoiced?: InvoiceActivityStatus;
}

@Injectable({
  providedIn: 'root',
})
export class InvoiceDataHelperService {
  private readonly store = inject(Store);

  public ACTIVITY_RELATIONS = [
    'activityCategory',
    'agreement',
    'agreement.client',
    'agreement.agreementProducts.product',
    'service',
    'shift',
    'shift.shiftCategory',
    'shift.user',
    'shift.labels',
    'shift.user.labels',
    'shift.user.organizationalUnit',
  ];

  public JOB_RELATIONS = [
    'job',
    'job.activities',
    'job.activities.service',
    'job.activities.activityCategory',
    'job.activities.endLocation',
    'job.activities.startLocation',
    'job.activities.profession',
    'job.organizationalUnit',
    'organizationalUnit',
  ];

  getWhere(filters: InvoiceFilters): PartialActivityWhereInput {
    const where: PartialActivityWhereInput = {
      ...this.getAgreementWhere(filters.agreements),
      operationalStatus: OperationStatus.Completed,
      shift: {
        state: {
          in: [ShiftState.AcceptedTimes, ShiftState.SubmittedToPayrollProvider],
        },
        userId: {
          in: filters.employees,
        },
        shiftCategoryId: {
          in: filters.shiftCategories,
        },
        organizationalUnitId: {
          in:
            this.store
              .selectSnapshot(AuthState.accessibleOrgUnits)
              ?.map((orgUnit) => orgUnit.id)
              .filter((id): id is string => id !== undefined) || [],
        },
        user: {
          organizationalUnitId: { in: filters.orgUnit },
        },
      },
      job: {
        organizationalUnitId: { in: filters.partners },
      },
      jobId: { in: filters.jobId },
      activityCategoryId: { in: filters.activityCategories },
      OR: filters.searchString
        ? [
            {
              name: {
                contains: filters.searchString,
                mode: 'insensitive',
              },
            },
            {
              shift: {
                name: {
                  contains: filters.searchString,
                  mode: 'insensitive',
                },
              },
            },
          ]
        : [],
    };

    if (filters.showServiceActivities) {
      where.serviceId = 'not-null';
    }

    if (filters.dateTypeFilterIndex === InvoiceDateTypeFilter.ByService) {
      where.startDatetime = new Date(filters.dateRange.start).toISOString();
      where.endDatetime = new Date(filters.dateRange.end).toISOString();
    }

    if (
      where.shift &&
      filters.dateTypeFilterIndex === InvoiceDateTypeFilter.ByShift
    ) {
      where.shift.reportedStartDatetime = {
        gte: new Date(filters.dateRange.start).toISOString(),
        lte: new Date(filters.dateRange.end).toISOString(),
      };
    }

    return where;
  }

  /**
   * The backend converts a top-level `where[agreementId]=null` into an
   * `IS NULL` check (NullConditionPipe). Combining "without agreement" with
   * specific agreements requires an OR group, wrapped in AND so it does not
   * clash with the search OR.
   */
  private getAgreementWhere(
    agreements: string[],
  ): Pick<PartialActivityWhereInput, 'agreementId' | 'AND'> {
    const agreementIds = agreements.filter((id) => id !== NO_AGREEMENT);
    const withoutAgreement = agreements.includes(NO_AGREEMENT);

    if (withoutAgreement && agreementIds.length) {
      return {
        AND: [
          {
            OR: [
              { agreementId: 'null' },
              { agreementId: { in: agreementIds } },
            ],
          },
        ],
      };
    }

    if (withoutAgreement) {
      return { agreementId: 'null' };
    }

    return { agreementId: { in: agreementIds } };
  }

  getInvoicing(filters: InvoiceFilters): Invoiced {
    let invoicing = {};
    if (filters.invoiced) {
      invoicing = {
        invoiced: filters.invoiced === InvoiceActivityStatus.Invoiced,
      };
    }
    return invoicing;
  }

  getShiftLabelIds(filters: InvoiceFilters): ShiftLabels {
    let shiftLabels = {};
    if (filters.shiftLabelsIds) {
      shiftLabels = {
        shiftLabelIds: filters.shiftLabelsIds,
      };
    }
    return shiftLabels;
  }

  getShiftProjectIds(filters: InvoiceFilters): ShiftProjects {
    let shiftProjects = {};
    if (filters.shiftProjectIds) {
      shiftProjects = {
        shiftProjectIds: filters.shiftProjectIds,
      };
    }
    return shiftProjects;
  }

  getShiftUserLabelIds(filters: InvoiceFilters): ShiftUserLabels {
    let shiftUserLabels = {};
    if (filters.shiftUserLabelIds) {
      shiftUserLabels = {
        shiftUserLabelIds: filters.shiftUserLabelIds,
      };
    }
    return shiftUserLabels;
  }

  getShiftUserProfessionIds(filters: InvoiceFilters): ShiftUserProfessions {
    let shiftUserProfessions = {};
    if (filters.profession) {
      shiftUserProfessions = {
        shiftUserProfessionIds: filters.profession,
      };
    }
    return shiftUserProfessions;
  }

  buildActivitiesOptions(
    filters: InvoiceFilters,
    pageNumber: number,
    pageSize: number,
  ): ActivitiesOptions {
    return {
      relations: this.ACTIVITY_RELATIONS,
      where: this.getWhere(filters),
      ...this.getInvoicing(filters),
      ...this.getShiftLabelIds(filters),
      ...this.getShiftUserLabelIds(filters),
      ...this.getShiftProjectIds(filters),
      ...this.getShiftUserProfessionIds(filters),
      pageNumber,
      pageSize,
      orderBy: [{ startDatetime: 'asc' }, { id: 'asc' }],
    };
  }
}
